export type EmarDailyTrackingField = {
  key: string;
  label: string;
  unit?: string;
  inputType?: "number" | "text";
  required?: boolean;
};

export type EmarDailyTrackingCategoryOption = {
  value: string;
  label: string;
  defaultName: string;
  guidance?: string;
  parameterSchema: EmarDailyTrackingField[];
};

const normalizeDailyTrackingCategoryLookup = (value: any) => (
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
);

export const EMAR_DAILY_TRACKING_CATEGORY_OPTIONS: EmarDailyTrackingCategoryOption[] = [
  {
    value: "blood_pressure",
    label: "Blood Pressure",
    defaultName: "Blood Pressure",
    guidance: "Track systolic and diastolic readings on the scheduled cadence.",
    parameterSchema: [
      { key: "systolic", label: "Systolic", unit: "mmHg", inputType: "number", required: true },
      { key: "diastolic", label: "Diastolic", unit: "mmHg", inputType: "number", required: true },
      { key: "position", label: "Position", inputType: "text" },
    ],
  },
  {
    value: "blood_glucose",
    label: "Blood Glucose",
    defaultName: "Blood Glucose",
    guidance: "Record the glucose reading and any meal context when needed.",
    parameterSchema: [
      { key: "glucose", label: "Glucose", unit: "mg/dL", inputType: "number", required: true },
      { key: "mealContext", label: "Meal context", inputType: "text" },
    ],
  },
  {
    value: "oxygen_therapy",
    label: "Oxygen / SpO2",
    defaultName: "Oxygen / SpO2",
    guidance: "Capture oxygen saturation and oxygen flow when ordered.",
    parameterSchema: [
      { key: "spo2", label: "SpO2", unit: "%", inputType: "number", required: true },
      { key: "oxygenLiters", label: "Oxygen flow", unit: "L/min", inputType: "number" },
    ],
  },
  {
    value: "pulse",
    label: "Pulse",
    defaultName: "Pulse",
    guidance: "Record pulse rate and rhythm notes when needed.",
    parameterSchema: [
      { key: "pulse", label: "Pulse", unit: "bpm", inputType: "number", required: true },
      { key: "rhythm", label: "Rhythm", inputType: "text" },
    ],
  },
  {
    value: "respiration",
    label: "Respiration",
    defaultName: "Respiration",
    guidance: "Record the respiratory rate and any breathing effort notes when needed.",
    parameterSchema: [
      { key: "respiratoryRate", label: "Respiratory Rate", unit: "breaths/min", inputType: "number", required: true },
      { key: "effort", label: "Effort", inputType: "text" },
    ],
  },
  {
    value: "temperature",
    label: "Temperature",
    defaultName: "Temperature",
    guidance: "Record the resident temperature using the preferred unit.",
    parameterSchema: [
      { key: "temperature", label: "Temperature", unit: "F", inputType: "number", required: true },
      { key: "site", label: "Site", inputType: "text" },
    ],
  },
  {
    value: "weight",
    label: "Weight",
    defaultName: "Weight",
    guidance: "Track the resident weight on the ordered schedule.",
    parameterSchema: [
      { key: "weight", label: "Weight", unit: "lb", inputType: "number", required: true },
    ],
  },
  {
    value: "pain_score",
    label: "Pain Score",
    defaultName: "Pain Score",
    guidance: "Record pain severity and any site or qualifier that matters for care.",
    parameterSchema: [
      { key: "painScore", label: "Pain score", unit: "/10", inputType: "number", required: true },
      { key: "painSite", label: "Pain site", inputType: "text" },
    ],
  },
  {
    value: "bowel_movement",
    label: "Bowel Movement",
    defaultName: "Bowel Movement",
    guidance: "Record bowel movement status and stool details when needed.",
    parameterSchema: [
      { key: "bowelMovement", label: "Status", inputType: "text", required: true },
      { key: "stoolNotes", label: "Stool notes", inputType: "text" },
    ],
  },
];

export const getEmarDailyTrackingCategoryOption = (value: any) => {
  const normalizedValue = normalizeDailyTrackingCategoryLookup(value);
  return EMAR_DAILY_TRACKING_CATEGORY_OPTIONS.find((option) => {
    const candidates = [
      option.value,
      option.label,
      option.defaultName,
    ];
    return candidates.some((candidate) => normalizeDailyTrackingCategoryLookup(candidate) === normalizedValue);
  }) || EMAR_DAILY_TRACKING_CATEGORY_OPTIONS[0];
};

export const normalizeEmarDailyTrackingCategoryValue = (value: any) => (
  getEmarDailyTrackingCategoryOption(value)?.value || "blood_pressure"
);

export const buildEmarDailyTrackingDefaultName = (category: any) => (
  getEmarDailyTrackingCategoryOption(category)?.defaultName || "Vital Tracker"
);

export const getEmarDailyTrackingSchema = (itemLike: any): EmarDailyTrackingField[] => {
  if (Array.isArray(itemLike?.targetSchema) && itemLike.targetSchema.length) {
    return itemLike.targetSchema;
  }
  return getEmarDailyTrackingCategoryOption(itemLike?.category)?.parameterSchema || [];
};

export const getEmarDailyTrackingProtocolCategory = (category: any) => {
  switch (normalizeEmarDailyTrackingCategoryValue(category)) {
    case "blood_pressure":
      return "bp_medication_hold";
    case "blood_glucose":
      return "sliding_scale_insulin";
    case "oxygen_therapy":
      return "oxygen_therapy";
    case "pulse":
      return "heart_rate_hold";
    case "respiration":
      return "respiration_monitoring";
    case "temperature":
      return "fever_medication";
    case "weight":
      return "weight_based_medication";
    case "pain_score":
      return "prn_pain_medication";
    default:
      return "custom_protocol";
  }
};

export const getEmarDailyTrackingFieldPlaceholder = (field: any) => {
  const key = String(field?.key || "").trim().toLowerCase();
  const label = String(field?.label || field?.key || "value").trim();
  if (key === "rhythm") {
    return "Example: regular, irregular, steady, weak";
  }
  if (key === "effort") {
    return "Example: unlabored, labored, shallow";
  }
  if (key === "position") {
    return "Example: sitting, standing, lying";
  }
  if (key === "mealcontext") {
    return "Example: before breakfast, after lunch";
  }
  if (key === "site") {
    return "Example: oral, axillary, temporal";
  }
  if (key === "bowelmovement") {
    return "Example: normal, loose, hard, none today";
  }
  if (key === "stoolnotes") {
    return "Example: soft, formed, color noted";
  }
  if (key === "painsite") {
    return "Example: left knee, lower back";
  }
  if (field?.unit) {
    return `Example: ${label.toLowerCase()} (${field.unit})`;
  }
  return `Example: ${label}`;
};

export const createEmptyEmarDailyTrackingItem = (category = "blood_pressure") => {
  const categoryOption = getEmarDailyTrackingCategoryOption(category);
  return {
    trackingItemId: "",
    name: categoryOption?.defaultName || "Vital Tracker",
    category: categoryOption?.value || category,
    instructions: "",
    frequency: "once_daily",
    schedule: "",
    scheduledClockTime: "",
    scheduleDayPattern: "daily",
    scheduleDays: [],
    scheduleMonthDays: [],
    effectiveStartDate: new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date()),
    effectiveEndDate: "",
    targetSchema: categoryOption?.parameterSchema || [],
    protocolEnabled: false,
    protocolDefinitions: [],
    protocolCategory: "",
    protocolInstructions: "",
    protocolRules: [],
    active: true,
    discontinued: false,
    discontinuedAt: "",
    discontinueReason: "",
  };
};

export const buildEmarDailyTrackingValueSummary = (entry: any, itemLike: any) => {
  const values = entry?.values && typeof entry.values === "object" ? entry.values : {};
  const schema = getEmarDailyTrackingSchema(itemLike);
  const parts = schema.map((field) => {
    const rawValue = values?.[field.key];
    if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") {
      return "";
    }
    return `${field.label}: ${String(rawValue).trim()}${field.unit ? ` ${field.unit}` : ""}`;
  }).filter(Boolean);
  return parts.join(", ");
};
